import { PbECommerceSettingsPlatformEnum } from '@itsrever/rever-api'
import { useAppSelector } from '@/redux/hooks'
import { useScopePortals } from '@/application/queries'
import { isReturnRateAnalyticsAllowed } from '@/utils'

// In the group view the loaded settings are the first portal's, so a platform-gated feature
// follows the group: it shows if any portal in it allows it. A selected legacy group without an id
// has no portal list, so it keeps reading the loaded settings.
function useScopeAllowsPlatform(allows: (platform?: string) => boolean) {
  const selectedEcommerce = useAppSelector(
    (s) => s.generalData.selectedEcommerce
  )
  const selectedGroup = useAppSelector((s) => s.generalData.selectedGroup)
  const platform = useAppSelector(
    (s) => s.settingsApi.settings.response.platform
  )
  const { portals } = useScopePortals()

  if (selectedEcommerce || (selectedGroup && !portals.length))
    return allows(platform)
  return portals.some((p) => allows(p.platform))
}

export function useReturnRateAnalyticsAllowed() {
  return useScopeAllowsPlatform(isReturnRateAnalyticsAllowed)
}

// The store on screen runs on the platform, or a portal of the group on screen does.
export function useScopeHasPlatform(platform: string) {
  return useScopeAllowsPlatform((p) => p === platform)
}

// The platforms whose returns portal offers the Full Catalog.
export const FULL_CATALOG_PLATFORMS: string[] = [
  PbECommerceSettingsPlatformEnum.Shopify,
  PbECommerceSettingsPlatformEnum.Prestashop,
  PbECommerceSettingsPlatformEnum.Woocommerce,
  PbECommerceSettingsPlatformEnum.Salesforce
]

export function useFullCatalogShown() {
  return useScopeAllowsPlatform(
    (platform) => !!platform && FULL_CATALOG_PLATFORMS.includes(platform)
  )
}

// The platforms whose integration can't create the exchange order, so their portals offer no exchanges.
const NO_EXCHANGES_PLATFORMS: string[] = [
  PbECommerceSettingsPlatformEnum.Shopware,
  PbECommerceSettingsPlatformEnum.Commercelayer,
  PbECommerceSettingsPlatformEnum.Inditex
]

export function useExchangesShown() {
  return useScopeAllowsPlatform(
    (platform) => !NO_EXCHANGES_PLATFORMS.includes(platform ?? '')
  )
}

// The platforms instant exchange runs on, as in settings-api's instantexchange.IsSupportedPlatform.
const INSTANT_EXCHANGE_PLATFORMS: string[] = [
  PbECommerceSettingsPlatformEnum.Shopify,
  PbECommerceSettingsPlatformEnum.Salesforce,
  PbECommerceSettingsPlatformEnum.Prestashop
]

export function useInstantExchangeShown() {
  return useScopeAllowsPlatform(
    (platform) => !!platform && INSTANT_EXCHANGE_PLATFORMS.includes(platform)
  )
}

export function useProductReturnReasonsShown() {
  return useScopeAllowsPlatform(
    (platform) => platform !== PbECommerceSettingsPlatformEnum.Inditex
  )
}
